// Page Détail Produit - Marketplace
// Mode Préouverture - Test

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Heart, Share2, MapPin, Clock, ShoppingCart, Star,
  Truck, Shield, Package, ChevronLeft, ChevronRight, Check,
  AlertTriangle, Info, Minus, Plus, Store, Tag, Zap, ExternalLink,
  MessageSquare, FileText, Phone, Mail, Send, X, Bot, Sparkles,
  Play, Video, Eye, BadgeCheck
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import { useBuyer } from '../../contexts/BuyerContext';
import { API_URL } from '../../config/api';

// ══════════════════════════════════════════════════════════════════════════════
// VENDEURS DÉMO - Boutiques avec CGV, Mentions légales, IA
// ══════════════════════════════════════════════════════════════════════════════

const DEMO_SELLERS = {
  'seller-elec-pro': {
    id: 'seller-elec-pro',
    name: 'Elec Pro Distribution',
    slug: 'elec-pro-distribution',
    description: 'Spécialiste en matériel électrique professionnel depuis 2005. Nous proposons une large gamme de produits des plus grandes marques à prix compétitifs.',
    logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=200&h=200&fit=crop',
    cover: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1200&h=400&fit=crop',
    verified: true,
    rating: 4.8,
    reviewsCount: 342,
    productsCount: 156,
    city: 'Lyon',
    phone: '04 72 00 00 00',
    email: 'contact@elecpro.fr',
    createdAt: '2005',
    certifications: ['Distributeur agréé', 'ISO 9001'],
    deliveryInfo: 'Livraison gratuite dès 100€. Expédition sous 24h pour les commandes passées avant 14h.',
    cgv: `CONDITIONS GÉNÉRALES DE VENTE - ELEC PRO DISTRIBUTION

1. OBJET
Les présentes conditions générales régissent les ventes de produits par Elec Pro Distribution.

2. PRIX
Les prix sont indiqués en euros TTC. Elec Pro Distribution se réserve le droit de modifier ses prix à tout moment.

3. COMMANDES
Toute commande implique l'acceptation des présentes conditions générales de vente.

4. LIVRAISON
- Livraison gratuite dès 100€ d'achat
- Expédition sous 24h (commandes avant 14h)
- Délai moyen : 3-5 jours ouvrés

5. RETOURS
Retour possible sous 14 jours. Les frais de retour sont à la charge du client sauf produit défectueux.

6. GARANTIE
Tous nos produits bénéficient de la garantie légale de conformité de 2 ans.`,
    mentionsLegales: `MENTIONS LÉGALES - ELEC PRO DISTRIBUTION

Raison sociale : Elec Pro Distribution SAS
Siège social : 45 rue de l'Industrie, 69003 Lyon
SIRET : 123 456 789 00012
TVA intracommunautaire : FR 12 345678901
Capital social : 50 000 €

Directeur de la publication : Jean Dupont
Hébergeur : AbracadaBati SAS`
  },
  'seller-plomb-expert': {
    id: 'seller-plomb-expert',
    name: 'Plomb Expert',
    slug: 'plomb-expert',
    description: 'Grossiste en plomberie et sanitaire. Plus de 10 000 références en stock permanent. Livraison rapide sur toute la France.',
    logo: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=200&h=200&fit=crop',
    cover: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=1200&h=400&fit=crop',
    verified: true,
    rating: 4.7,
    reviewsCount: 528,
    productsCount: 423,
    city: 'Marseille',
    phone: '04 91 00 00 00',
    email: 'pro@plombexpert.fr',
    createdAt: '2010',
    certifications: ['Grossiste agréé', 'NF Service'],
    deliveryInfo: 'Livraison express disponible. Retrait en entrepôt possible.',
    cgv: `CONDITIONS GÉNÉRALES DE VENTE - PLOMB EXPERT

1. Nos produits sont garantis conformes aux normes en vigueur.
2. Livraison sous 48-72h en France métropolitaine.
3. Retours acceptés sous 30 jours (produits non utilisés).
4. Paiement sécurisé par CB, virement ou PayPal.`,
    mentionsLegales: `MENTIONS LÉGALES - PLOMB EXPERT

Raison sociale : Plomb Expert SARL
Siège : 12 avenue du Commerce, 13008 Marseille
SIRET : 987 654 321 00045`
  },
  'seller-outillage-pro': {
    id: 'seller-outillage-pro',
    name: 'Outillage Pro France',
    slug: 'outillage-pro-france',
    description: 'Le spécialiste de l\'outillage professionnel. Bosch, Makita, DeWalt, Milwaukee... Toutes les grandes marques au meilleur prix.',
    logo: 'https://images.unsplash.com/photo-1581783898377-1c85bf937427?w=200&h=200&fit=crop',
    cover: 'https://images.unsplash.com/photo-1530124566582-a618bc2615dc?w=1200&h=400&fit=crop',
    verified: true,
    rating: 4.9,
    reviewsCount: 1205,
    productsCount: 892,
    city: 'Paris',
    phone: '01 42 00 00 00',
    email: 'contact@outillagepro.fr',
    createdAt: '1998',
    certifications: ['Revendeur officiel', 'Service après-vente agréé'],
    deliveryInfo: 'Livraison gratuite dès 50€. Service SAV avec atelier de réparation.',
    cgv: `CONDITIONS GÉNÉRALES DE VENTE - OUTILLAGE PRO FRANCE

Article 1 - Prix : TTC, modifiables sans préavis.
Article 2 - Livraison : Gratuite dès 50€, sous 24-48h.
Article 3 - Garantie : 2 ans minimum, 3 ans sur les batteries.
Article 4 - SAV : Atelier agréé, réparations sous 10 jours.
Article 5 - Retours : 30 jours, produit neuf non utilisé.`,
    mentionsLegales: `MENTIONS LÉGALES - OUTILLAGE PRO FRANCE

SAS Outillage Pro France
15 rue des Artisans, 75011 Paris
RCS Paris 456 789 123
Capital : 200 000 €`
  },
  'default': {
    id: 'default-seller',
    name: 'BatiAssist Pro',
    slug: 'batiassist-pro',
    description: 'Boutique officielle AbracadaBati. Produits sélectionnés pour les professionnels du bâtiment.',
    logo: null,
    verified: true,
    rating: 4.5,
    reviewsCount: 89,
    productsCount: 45,
    city: 'France',
    phone: '01 00 00 00 00',
    email: 'contact@abracadabati.com',
    createdAt: '2024',
    certifications: [],
    deliveryInfo: 'Livraison standard sous 3-5 jours.',
    cgv: 'Conditions générales de vente disponibles prochainement.',
    mentionsLegales: 'Mentions légales disponibles prochainement.'
  }
};

// Fonction pour obtenir le vendeur d'un produit
const getSellerForProduct = (product) => {
  // Mapper les catégories vers les vendeurs
  const categorySellerMap = {
    'electricite': 'seller-elec-pro',
    'plomberie': 'seller-plomb-expert',
    'outillage': 'seller-outillage-pro'
  };
  
  const sellerId = product.seller_id || categorySellerMap[product.category] || 'default';
  return DEMO_SELLERS[sellerId] || DEMO_SELLERS['default'];
};

// ══════════════════════════════════════════════════════════════════════════════
// VIDÉOS & POSTS DU RÉSEAU SOCIAL LIÉS AUX PRODUITS
// ══════════════════════════════════════════════════════════════════════════════

const DEMO_LINKED_MEDIA = {
  // Posts/Reels liés aux produits (par product_id ou par correspondance)
  products: {
    'prod-1': [
      {
        id: 'media_1',
        type: 'reel',
        thumbnail: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=400&h=500&fit=crop',
        author: { name: 'Martin Plomberie', color: '#3B82F6', verified: true },
        views: 12500,
        likes: 234,
        duration: 45,
        caption: 'Installation du robinet mitigeur - Tutoriel complet',
        timeAgo: 'Il y a 3j'
      },
      {
        id: 'media_2',
        type: 'photo',
        thumbnail: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=400&h=500&fit=crop',
        author: { name: 'Sophie Martin', color: '#EC4899', verified: false },
        likes: 89,
        caption: 'Super satisfaite de mon nouveau robinet !',
        timeAgo: 'Il y a 1 sem'
      }
    ],
    'prod-2': [
      {
        id: 'media_3',
        type: 'reel',
        thumbnail: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400&h=500&fit=crop',
        author: { name: 'Élec Express', color: '#EAB308', verified: true },
        views: 8700,
        likes: 156,
        duration: 60,
        caption: 'Test du disjoncteur différentiel - Sécurité maximale',
        timeAgo: 'Il y a 5j'
      }
    ],
    'default': [
      {
        id: 'media_default_1',
        type: 'reel',
        thumbnail: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=500&fit=crop',
        author: { name: 'Pro du Bâtiment', color: '#10B981', verified: true },
        views: 5600,
        likes: 112,
        duration: 30,
        caption: 'Démonstration produit en conditions réelles',
        timeAgo: 'Il y a 2j'
      }
    ]
  },
  // Posts/Reels liés aux annonces (par annonce_id)
  annonces: {
    'ann-1': [
      {
        id: 'media_ann_1',
        type: 'reel',
        thumbnail: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=400&h=500&fit=crop',
        author: { name: 'Sophie Déco', color: '#EC4899', verified: false },
        views: 3400,
        likes: 78,
        duration: 25,
        caption: 'Ma perceuse à vendre - État neuf !',
        timeAgo: 'Il y a 3j'
      }
    ],
    'default': []
  },
  // Posts/Reels liés aux prestations de service
  services: {
    'serv-1': [
      {
        id: 'media_serv_1',
        type: 'reel',
        thumbnail: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400&h=500&fit=crop',
        author: { name: 'Élec Express', color: '#EAB308', verified: true },
        views: 15200,
        likes: 345,
        duration: 90,
        caption: 'Installation tableau électrique - Timelapse du chantier',
        timeAgo: 'Il y a 1 sem'
      },
      {
        id: 'media_serv_2',
        type: 'photo',
        thumbnail: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=500&fit=crop',
        author: { name: 'Client Satisfait', color: '#22C55E', verified: false },
        likes: 67,
        caption: 'Travail impeccable, je recommande !',
        timeAgo: 'Il y a 4j'
      }
    ],
    'default': []
  }
};

// Fonction pour récupérer les médias liés à un élément
const getLinkedMedia = (type, itemId) => {
  const mediaMap = DEMO_LINKED_MEDIA[type] || {};
  return mediaMap[itemId] || mediaMap['default'] || [];
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Section Vidéos & Posts du Réseau Social
// ══════════════════════════════════════════════════════════════════════════════

function LinkedMediaSection({ type, itemId, itemName }) {
  const navigate = useNavigate();
  const linkedMedia = getLinkedMedia(type, itemId);
  
  if (linkedMedia.length === 0) return null;
  
  return (
    <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Video size={20} className="text-[#F97316]" />
          <h2 className="text-lg font-bold text-gray-900">Vidéos & Posts associés</h2>
        </div>
        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
          DÉMO
        </span>
      </div>
      
      <p className="text-sm text-gray-500 mb-4">
        Découvre ce que la communauté partage sur ce {type === 'products' ? 'produit' : type === 'annonces' ? 'annonce' : 'service'}
      </p>
      
      {/* Grille de médias */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {linkedMedia.map((media) => (
          <motion.div
            key={media.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(`/reseau/reels?post=${media.id}`)}
            className="relative aspect-[4/5] rounded-xl overflow-hidden cursor-pointer group"
          >
            {/* Thumbnail */}
            <img 
              src={media.thumbnail} 
              alt="" 
              className="w-full h-full object-cover"
            />
            
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
            
            {/* Badge Reel/Photo */}
            {media.type === 'reel' && (
              <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-1 rounded-full">
                <Play size={10} fill="white" />
                {Math.floor(media.duration / 60)}:{(media.duration % 60).toString().padStart(2, '0')}
              </div>
            )}
            
            {/* Vues (pour les reels) */}
            {media.type === 'reel' && media.views && (
              <div className="absolute bottom-12 left-2 flex items-center gap-1 text-white text-xs">
                <Eye size={12} />
                {media.views.toLocaleString()}
              </div>
            )}
            
            {/* Author info */}
            <div className="absolute bottom-2 left-2 right-2">
              <div className="flex items-center gap-2">
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: media.author.color }}
                >
                  {media.author.name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="text-white text-xs font-medium truncate">
                      {media.author.name.split(' ')[0]}
                    </span>
                    {media.author.verified && (
                      <BadgeCheck size={10} className="text-blue-400 fill-blue-400 flex-shrink-0" />
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                <Play size={24} className="text-white" fill="white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Lien vers plus */}
      <Link 
        to="/reseau"
        className="mt-4 flex items-center justify-center gap-2 text-sm text-[#F97316] font-medium hover:underline"
      >
        Voir plus sur le Réseau
        <ExternalLink size={14} />
      </Link>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Assistant IA du Vendeur - VERSION LICORNE (Même charte que Artisan)
// ══════════════════════════════════════════════════════════════════════════════

function VendeurAIChat({ seller, isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = React.useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 1,
        role: 'assistant',
        content: `Bonjour ! Je suis l'assistant IA de **${seller.name}**.\n\nJe suis paramétré pour aider les clients de cette boutique, répondre aux questions sur nos produits, les livraisons et les services.\n\nComme il s'agit d'une **version démo**, je ne peux pas traiter une vraie commande. Mais je peux t'aider avec :\n• Nos produits et disponibilités\n• Les délais de livraison\n• Les conditions de retour\n• Le fonctionnement d'AbracadaBati\n\nComment puis-je t'aider ?`
      }]);
    }
  }, [isOpen, seller.name]);

  // Formatage du texte avec **bold**
  const formatMessage = (text) => {
    return text.split('\n').map((line, i) => (
      <span key={i}>
        {line.split(/(\*\*[^*]+\*\*)/).map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j} className="font-semibold">{part.slice(2, -2)}</strong>;
          }
          return part;
        })}
        {i < text.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  const generateResponse = (question) => {
    const q = question.toLowerCase();
    
    if (q.includes('livraison') || q.includes('délai')) {
      return `**Informations livraison ${seller.name}**\n\n${seller.deliveryInfo}\n\nBesoin de plus de détails ?`;
    }
    if (q.includes('retour') || q.includes('rembours')) {
      return `**Politique de retour**\n\nChez ${seller.name}, tu peux retourner tes articles sous 14 à 30 jours selon les conditions.\n\nConsulte nos CGV pour plus de détails ou contacte-nous directement.`;
    }
    if (q.includes('contact') || q.includes('téléphone') || q.includes('email')) {
      return `**Nous contacter**\n\n• Téléphone : ${seller.phone}\n• Email : ${seller.email}\n• Ville : ${seller.city}\n\nNotre équipe est disponible du lundi au vendredi de 9h à 18h.`;
    }
    if (q.includes('garantie')) {
      return `**Garantie**\n\nTous nos produits bénéficient de la garantie légale de conformité de 2 ans minimum.\n\nCertains produits ont une garantie étendue - consulte la fiche produit pour plus de détails.`;
    }
    if (q.includes('paiement') || q.includes('payer')) {
      return `**Moyens de paiement**\n\nNous acceptons :\n• Carte bancaire (Visa, Mastercard)\n• Virement bancaire\n• PayPal\n\nPaiement 100% sécurisé.`;
    }
    if (q.includes('abracadabati') || q.includes('plateforme') || q.includes('comment ça marche')) {
      return `**AbracadaBati** est le premier centre commercial virtuel du bâtiment !\n\n**Pour les particuliers :**\n• Trouve des produits et artisans vérifiés\n• Compare les prix\n• Bénéficie d'un assistant IA 24/7\n\n**Pour les pros :**\n• Vitrine professionnelle\n• CRM intégré\n• **Zéro commission** sur les ventes\n\nQue souhaites-tu savoir d'autre ?`;
    }
    
    return `Merci pour ta question ! En tant qu'assistant IA en version démo, je suis limité dans mes réponses spécifiques.\n\nMais je peux t'aider avec :\n• Le fonctionnement d'AbracadaBati\n• Les infos livraison et retours\n• Les garanties et paiements\n\nQue souhaites-tu savoir ?`;
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMessage = { id: Date.now(), role: 'user', content: inputValue };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    setTimeout(() => {
      const response = generateResponse(inputValue);
      setMessages(prev => [...prev, { id: Date.now(), role: 'assistant', content: response }]);
      setIsTyping(false);
    }, 1200);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25 }}
          className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl overflow-hidden shadow-2xl"
          style={{ maxHeight: '85vh' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header ORANGE Premium - Identique à Artisan */}
          <div className="bg-gradient-to-r from-[#F97316] to-[#EA580C] p-4 text-white relative overflow-hidden">
            {/* Cercles décoratifs */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full" />
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/5 rounded-full" />
            
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Bot size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Assistant IA</h3>
                  <p className="text-sm text-white/90">{seller.name}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="relative flex items-center gap-2 mt-3 text-sm text-white/95">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span>En ligne • Réponse immédiate 24/7</span>
            </div>
          </div>

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white">
            {messages.map(msg => (
              <motion.div 
                key={msg.id} 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center mr-2 flex-shrink-0">
                    <Bot size={16} className="text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user' 
                    ? 'bg-[#F97316] text-white rounded-br-md' 
                    : 'bg-white shadow-md border border-gray-100 rounded-bl-md text-gray-700'
                }`}>
                  <p className="text-sm leading-relaxed">{formatMessage(msg.content)}</p>
                </div>
              </motion.div>
            ))}
            {isTyping && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center mr-2 flex-shrink-0">
                  <Bot size={16} className="text-white" />
                </div>
                <div className="bg-white shadow-md border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleSend()}
                placeholder="Pose ta question..."
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316]/20"
              />
              <Button onClick={handleSend} className="bg-[#F97316] hover:bg-[#EA580C] rounded-xl px-4">
                <Send size={18} />
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Assistant IA en version démo • AbracadaBati Marketplace
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Bouton Flottant IA Mobile - Identique à ShopDetailPage
// ══════════════════════════════════════════════════════════════════════════════

function SmartAIFab({ sellerName, onClick }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  
  useEffect(() => {
    setAnimationPhase(0);
    setIsExpanded(false);
    
    // Phase 1: Attendre 2 secondes puis ouvrir
    const timer1 = setTimeout(() => {
      setIsExpanded(true);
      setAnimationPhase(1);
    }, 2000);
    
    // Phase 2: Fermer après 3 secondes
    const timer2 = setTimeout(() => {
      setIsExpanded(false);
      setAnimationPhase(2);
    }, 5000);
    
    // Phase 3: Rouvrir après 5 secondes de plus
    const timer3 = setTimeout(() => {
      setIsExpanded(true);
      setAnimationPhase(3);
    }, 10000);
    
    // Phase 4: Fermer définitivement
    const timer4 = setTimeout(() => {
      setIsExpanded(false);
      setAnimationPhase(4);
    }, 13000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);
  
  const shortName = sellerName?.split(' ').slice(0, 2).join(' ') || 'le vendeur';
  
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.5, type: 'spring', stiffness: 200, damping: 15 }}
      className="fixed bottom-24 right-4 z-[9000] lg:hidden"
    >
      <motion.button
        onClick={onClick}
        className="flex items-center bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white shadow-2xl rounded-full overflow-hidden"
        style={{
          boxShadow: '0 8px 32px rgba(249, 115, 22, 0.45)'
        }}
        animate={{
          paddingLeft: isExpanded ? 16 : 16,
          paddingRight: isExpanded ? 16 : 16,
          x: isExpanded ? -8 : 0,
        }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        data-testid="fab-ai-chat-marketplace"
      >
        {/* Icon container */}
        <motion.div 
          className="flex items-center justify-center flex-shrink-0"
          animate={{
            width: 56,
            height: 56,
          }}
        >
          <Bot size={26} />
        </motion.div>
        
        {/* Texte animé */}
        <AnimatePresence mode="wait">
          {isExpanded && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'auto', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              className="overflow-hidden whitespace-nowrap pr-2"
            >
              <span className="font-semibold text-sm">
                Assistant IA de {shortName}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Pulse effect */}
        {!isExpanded && animationPhase < 4 && (
          <>
            <span className="absolute inset-0 rounded-full bg-white/20 animate-ping opacity-30" style={{ animationDuration: '2s' }} />
            <span className="absolute inset-1 rounded-full border-2 border-white/30" />
          </>
        )}
      </motion.button>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES DE TEST - PRÉOUVERTURE MARKETPLACE
// ══════════════════════════════════════════════════════════════════════════════

const MOCK_PRODUITS = [
  {
    id: 'prod-1',
    title: 'Perceuse visseuse sans fil Bosch Professional GSR 18V-60',
    description: 'Perceuse-visseuse sans fil 18V avec moteur brushless pour une longue durée de vie. Couple max 60 Nm. Livrée avec 2 batteries 4.0Ah ProCORE, chargeur rapide GAL 18V-40 et coffret L-BOXX. Idéale pour les professionnels exigeants.',
    price: 289.99,
    originalPrice: 349.99,
    discount: 17,
    rating: 4.8,
    reviewCount: 234,
    category: 'outillage',
    brand: 'Bosch Professional',
    inStock: true,
    stockQuantity: 45,
    photos: [
      'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800',
      'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=800'
    ],
    features: ['Moteur brushless', '2 batteries 4.0Ah', 'Couple 60 Nm', 'Coffret L-BOXX'],
    seller: { id: 'seller-outillage-pro', name: 'Outillage Pro France', verified: true, rating: 4.9 },
    delivery: { express: true, free: true, delay: '24-48h' }
  },
  {
    id: 'prod-2',
    title: 'Carrelage grès cérame effet béton 60x60cm - Gris anthracite',
    description: 'Carrelage grand format en grès cérame rectifié. Effet béton ciré contemporain. Résistant au gel et antidérapant R10. Idéal intérieur/extérieur. Vendu par lot de 1.44m² (4 carreaux).',
    price: 34.90,
    originalPrice: null,
    discount: 0,
    rating: 4.6,
    reviewCount: 89,
    category: 'carrelage',
    brand: 'Marazzi',
    inStock: true,
    stockQuantity: 230,
    photos: [
      'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=800'
    ],
    features: ['60x60cm rectifié', 'Effet béton ciré', 'Antidérapant R10', 'Int/Ext'],
    seller: { name: 'CarroConcept', verified: true, rating: 4.7 },
    delivery: { express: false, free: false, delay: '3-5 jours', cost: 9.90 }
  },
  {
    id: 'prod-3',
    title: 'Peinture acrylique mate Tollens - Blanc absolu 10L',
    description: 'Peinture murale monocouche haute qualité. Finition mate veloutée. Excellente opacité et pouvoir couvrant (12m²/L). Séchage rapide, lessivable. Certification A+ faibles émissions.',
    price: 79.90,
    originalPrice: 94.90,
    discount: 16,
    rating: 4.9,
    reviewCount: 456,
    category: 'peinture',
    brand: 'Tollens',
    inStock: true,
    stockQuantity: 120,
    photos: [
      'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800'
    ],
    features: ['Monocouche', 'Mat velouté', 'Lessivable', 'A+ faibles émissions'],
    seller: { name: 'ColorPro', verified: true, rating: 4.8 },
    delivery: { express: true, free: true, delay: '24-48h' }
  },
  {
    id: 'prod-4',
    title: 'Mitigeur thermostatique douche Grohe Grohtherm 1000',
    description: 'Mitigeur thermostatique mural pour douche. Technologie TurboStat pour température stable. Butée de sécurité 38°C. Corps lisse pour nettoyage facile. Finition StarLight chrome brillant.',
    price: 159.00,
    originalPrice: 189.00,
    discount: 16,
    rating: 4.7,
    reviewCount: 178,
    category: 'plomberie',
    brand: 'Grohe',
    inStock: true,
    stockQuantity: 67,
    photos: [
      'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800'
    ],
    features: ['Thermostatique', 'TurboStat', 'Sécurité 38°C', 'Chrome StarLight'],
    seller: { name: 'SaniPro Express', verified: true, rating: 4.9 },
    delivery: { express: true, free: true, delay: '24h' }
  },
  {
    id: 'prod-5',
    title: 'Panneau solaire monocristallin 400W - Longi Hi-MO 5',
    description: 'Panneau photovoltaïque haute performance. Technologie Half-Cut pour meilleur rendement. Rendement 21.3%. Garantie produit 12 ans, garantie performance linéaire 25 ans.',
    price: 189.00,
    originalPrice: 229.00,
    discount: 17,
    rating: 4.9,
    reviewCount: 312,
    category: 'solaire',
    brand: 'Longi',
    inStock: true,
    stockQuantity: 89,
    photos: [
      'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800'
    ],
    features: ['400W Half-Cut', 'Rendement 21.3%', 'Garantie 25 ans', 'Certifié TÜV'],
    seller: { name: 'SolarTech Pro', verified: true, rating: 4.8 },
    delivery: { express: false, free: true, delay: '3-5 jours' }
  },
  {
    id: 'prod-6',
    title: 'Scie circulaire plongeante Festool TS 55 REBQ-Plus',
    description: 'La référence des scies plongeantes. Moteur 1200W, profondeur de coupe 55mm. Système anti-recul FastFix. Compatible rails de guidage. Livrée en Systainer.',
    price: 549.00,
    originalPrice: null,
    discount: 0,
    rating: 4.9,
    reviewCount: 567,
    category: 'menuiserie',
    brand: 'Festool',
    inStock: true,
    stockQuantity: 23,
    photos: [
      'https://images.unsplash.com/photo-1616401784845-180882ba9ba8?w=800'
    ],
    features: ['1200W', 'Coupe 55mm', 'Anti-recul', 'Systainer'],
    seller: { name: 'OutillagePro', verified: true, rating: 4.9 },
    delivery: { express: true, free: true, delay: '24-48h' }
  },
  {
    id: 'prod-7',
    title: 'Radiateur électrique connecté Atlantic Divali 1500W',
    description: 'Radiateur à inertie fonte connecté. Programmation intelligente via app Cozytouch. Détection fenêtre ouverte et présence. Design épuré blanc. Garantie 3 ans.',
    price: 449.00,
    originalPrice: 529.00,
    discount: 15,
    rating: 4.7,
    reviewCount: 234,
    category: 'chauffage',
    brand: 'Atlantic',
    inStock: true,
    stockQuantity: 56,
    photos: [
      'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800'
    ],
    features: ['Connecté WiFi', 'Inertie fonte', 'Détection présence', 'App Cozytouch'],
    seller: { name: 'ChauffagePro', verified: true, rating: 4.8 },
    delivery: { express: true, free: true, delay: '24-48h' }
  },
  {
    id: 'prod-8',
    title: 'Échafaudage roulant aluminium - Hauteur 6m',
    description: 'Échafaudage professionnel en aluminium léger. Montage rapide sans outil. Plateaux antidérapants, garde-corps intégrés. Roulettes avec freins. Charge max 200kg/m².',
    price: 789.00,
    originalPrice: 899.00,
    discount: 12,
    rating: 4.6,
    reviewCount: 78,
    category: 'maconnerie',
    brand: 'Tubesca-Comabi',
    inStock: true,
    stockQuantity: 18,
    photos: [
      'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800'
    ],
    features: ['Aluminium léger', 'Hauteur 6m', 'Montage sans outil', 'Charge 200kg/m²'],
    seller: { name: 'MatérielPro BTP', verified: true, rating: 4.7 },
    delivery: { express: false, free: true, delay: '5-7 jours' }
  },
  {
    id: 'prod-9',
    title: 'Robot tondeuse Husqvarna Automower 310 Mark II',
    description: 'Robot tondeuse connecté pour surfaces jusqu\'à 1000m². Navigation GPS assistée. Gestion multi-zones. App Automower Connect. Ultra silencieux 58dB.',
    price: 1299.00,
    originalPrice: 1499.00,
    discount: 13,
    rating: 4.8,
    reviewCount: 189,
    category: 'jardin',
    brand: 'Husqvarna',
    inStock: true,
    stockQuantity: 12,
    photos: [
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800'
    ],
    features: ['Jusqu\'à 1000m²', 'GPS assisté', 'App connectée', 'Ultra silencieux'],
    seller: { name: 'JardinTech', verified: true, rating: 4.9 },
    delivery: { express: true, free: true, delay: '48-72h' }
  },
  {
    id: 'prod-10',
    title: 'Robot piscine Dolphin E40i - WiFi connecté',
    description: 'Robot nettoyeur électrique pour piscines jusqu\'à 12m. Nettoie fond, parois et ligne d\'eau. Pilotage via smartphone. Brosses combinées PVC/mousse. Cycle 2h.',
    price: 899.00,
    originalPrice: 1049.00,
    discount: 14,
    rating: 4.7,
    reviewCount: 145,
    category: 'piscine',
    brand: 'Dolphin',
    inStock: true,
    stockQuantity: 34,
    photos: [
      'https://images.unsplash.com/photo-1575429198097-0414ec08e8cd?w=800'
    ],
    features: ['WiFi connecté', 'Fond/parois/ligne eau', 'Cycle 2h', 'Jusqu\'à 12m'],
    seller: { name: 'PiscineShop Pro', verified: true, rating: 4.8 },
    delivery: { express: true, free: true, delay: '24-48h' }
  },
  {
    id: 'prod-11',
    title: 'Perforateur burineur Makita HR2470 SDS-Plus',
    description: 'Perforateur 3 modes : perçage, perfo-béton, burinage. Puissance 780W, énergie de frappe 2.4J. Mandrin SDS-Plus. Coffret avec jeu de forets et burins.',
    price: 189.00,
    originalPrice: 219.00,
    discount: 14,
    rating: 4.8,
    reviewCount: 423,
    category: 'electricite',
    brand: 'Makita',
    inStock: true,
    stockQuantity: 78,
    photos: [
      'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800'
    ],
    features: ['780W', '3 modes', 'SDS-Plus', 'Coffret complet'],
    seller: { name: 'BatiPro Distribution', verified: true, rating: 4.9 },
    delivery: { express: true, free: true, delay: '24h' }
  },
  {
    id: 'prod-12',
    title: 'Pompe à chaleur air-eau Daikin Altherma 3 - 8kW',
    description: 'PAC monobloc R32 haute efficacité. Chauffage + ECS + rafraîchissement. COP jusqu\'à 5.1. Compatible plancher chauffant et radiateurs. Pilotage Daikin Residential Controller.',
    price: 4890.00,
    originalPrice: 5490.00,
    discount: 11,
    rating: 4.9,
    reviewCount: 89,
    category: 'chauffage',
    brand: 'Daikin',
    inStock: true,
    stockQuantity: 8,
    photos: [
      'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800'
    ],
    features: ['8kW R32', 'COP 5.1', 'Chauffage/ECS/Clim', 'App connectée'],
    seller: { name: 'ClimaPro Solutions', verified: true, rating: 4.9 },
    delivery: { express: false, free: true, delay: '7-10 jours' }
  },
  {
    id: 'prod-13',
    title: 'Coupe-carreaux électrique Rubi DC-250 1200',
    description: 'Table de coupe professionnelle. Longueur de coupe 102cm, diagonale 72cm. Moteur 1500W. Disque diamant 250mm inclus. Bac eau avec système de recirculation.',
    price: 1249.00,
    originalPrice: null,
    discount: 0,
    rating: 4.8,
    reviewCount: 156,
    category: 'carrelage',
    brand: 'Rubi',
    inStock: true,
    stockQuantity: 15,
    photos: [
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800'
    ],
    features: ['Coupe 102cm', '1500W', 'Disque 250mm', 'Bac recirculation'],
    seller: { name: 'CarroConcept', verified: true, rating: 4.7 },
    delivery: { express: false, free: true, delay: '3-5 jours' }
  },
  {
    id: 'prod-14',
    title: 'Isolant laine de roche Rockwool Rockplus 200mm - R=5.7',
    description: 'Panneau semi-rigide pour isolation des combles perdus. Haute performance thermique R=5.7. Classement feu A1 incombustible. Pack de 3m² (2 panneaux 1200x1000mm).',
    price: 42.90,
    originalPrice: 49.90,
    discount: 14,
    rating: 4.7,
    reviewCount: 234,
    category: 'isolation',
    brand: 'Rockwool',
    inStock: true,
    stockQuantity: 340,
    photos: [
      'https://images.unsplash.com/photo-1607400201889-565b1ee75f8e?w=800'
    ],
    features: ['R=5.7', '200mm', 'Incombustible A1', 'Pack 3m²'],
    seller: { name: 'IsoPro Distribution', verified: true, rating: 4.6 },
    delivery: { express: false, free: false, delay: '3-5 jours', cost: 14.90 }
  },
  {
    id: 'prod-15',
    title: 'Climatiseur réversible Mitsubishi MSZ-AP35VG - 3.5kW',
    description: 'Unité murale inverter A+++. Technologie Plasma Quad filtration. Mode silencieux 19dB. WiFi intégré pour pilotage à distance. Garantie 5 ans pièces.',
    price: 1190.00,
    originalPrice: 1390.00,
    discount: 14,
    rating: 4.8,
    reviewCount: 267,
    category: 'climatisation',
    brand: 'Mitsubishi Electric',
    inStock: true,
    stockQuantity: 28,
    photos: [
      'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800'
    ],
    features: ['3.5kW A+++', 'Inverter', 'WiFi intégré', 'Silencieux 19dB'],
    seller: { name: 'ClimaPro Solutions', verified: true, rating: 4.9 },
    delivery: { express: true, free: true, delay: '48-72h' }
  },
  {
    id: 'prod-16',
    title: 'Fenêtre de toit Velux GGL UK04 - 134x98cm',
    description: 'Fenêtre à rotation en bois massif. Double vitrage Confort isolation renforcée. Finition vernis incolore. Barre de manoeuvre en partie haute. Compatible volet roulant.',
    price: 489.00,
    originalPrice: 549.00,
    discount: 11,
    rating: 4.7,
    reviewCount: 312,
    category: 'renovation',
    brand: 'Velux',
    inStock: true,
    stockQuantity: 42,
    photos: [
      'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800'
    ],
    features: ['134x98cm', 'Bois massif', 'Double vitrage', 'Rotation'],
    seller: { name: 'MatériauxPro', verified: true, rating: 4.8 },
    delivery: { express: false, free: true, delay: '5-7 jours' }
  },
  {
    id: 'prod-17',
    title: 'Meuleuse angulaire Hilti AG 125-19SE - 1900W',
    description: 'Meuleuse professionnelle avec Active Torque Control. Disque 125mm, 1900W. Système anti-kickback. Poignée latérale AVR anti-vibrations. Coffret de transport.',
    price: 329.00,
    originalPrice: 379.00,
    discount: 13,
    rating: 4.9,
    reviewCount: 198,
    category: 'electricite',
    brand: 'Hilti',
    inStock: true,
    stockQuantity: 34,
    photos: [
      'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=800'
    ],
    features: ['1900W', 'Anti-kickback', 'AVR anti-vibrations', 'Coffret'],
    seller: { name: 'OutillagePro', verified: true, rating: 4.9 },
    delivery: { express: true, free: true, delay: '24-48h' }
  },
  {
    id: 'prod-18',
    title: 'Bétonnière électrique Altrad BT180 - Cuve 180L',
    description: 'Bétonnière professionnelle 180L de capacité de malaxage. Moteur 800W. Couronne fonte et pignons traités. Châssis renforcé avec roues. Malaxage homogène.',
    price: 389.00,
    originalPrice: 449.00,
    discount: 13,
    rating: 4.6,
    reviewCount: 87,
    category: 'maconnerie',
    brand: 'Altrad',
    inStock: true,
    stockQuantity: 19,
    photos: [
      'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800'
    ],
    features: ['180L', '800W', 'Couronne fonte', 'Châssis renforcé'],
    seller: { name: 'MatérielPro BTP', verified: true, rating: 4.7 },
    delivery: { express: false, free: true, delay: '5-7 jours' }
  },
  {
    id: 'prod-19',
    title: 'Parquet contrecollé chêne naturel 14mm - Click',
    description: 'Parquet 3 plis pose flottante Click. Parement chêne français 3.5mm. Finition huilée naturelle mat. Compatible chauffage au sol. Pack de 2.2m².',
    price: 64.90,
    originalPrice: 74.90,
    discount: 13,
    rating: 4.8,
    reviewCount: 345,
    category: 'menuiserie',
    brand: 'Panaget',
    inStock: true,
    stockQuantity: 156,
    photos: [
      'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=800'
    ],
    features: ['Chêne français', 'Click flottant', 'Huilé mat', 'Pack 2.2m²'],
    seller: { name: 'ParquetPro', verified: true, rating: 4.8 },
    delivery: { express: false, free: false, delay: '3-5 jours', cost: 12.90 }
  },
  {
    id: 'prod-20',
    title: 'Taille-haie thermique Stihl HS 82 RC-E - 75cm',
    description: 'Taille-haie professionnel à lames doubles. Moteur 2-MIX 22.7cc. Lame 75cm coupe simple. Système ErgoStart démarrage facile. Poignée rotative multi-positions.',
    price: 489.00,
    originalPrice: null,
    discount: 0,
    rating: 4.9,
    reviewCount: 234,
    category: 'jardin',
    brand: 'Stihl',
    inStock: true,
    stockQuantity: 21,
    photos: [
      'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800'
    ],
    features: ['Lame 75cm', '2-MIX 22.7cc', 'ErgoStart', 'Poignée rotative'],
    seller: { name: 'JardinTech', verified: true, rating: 4.9 },
    delivery: { express: true, free: true, delay: '24-48h' }
  }
];

export { MOCK_PRODUITS };

export default function ProduitDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showAIChat, setShowAIChat] = useState(false);
  const [showCGV, setShowCGV] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  
  const { 
    addToCart, 
    toggleFavorite, 
    isFavorite: contextIsFavorite 
  } = useBuyer();

  const isFavorite = product ? contextIsFavorite(product.id) : false;
  
  // Récupérer les infos complètes du vendeur
  const seller = product ? getSellerForProduct(product) : null;

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      // PRIORITÉ 1: Chercher via l'API Marketplace (produits démo + produits réels)
      const response = await fetch(`${API_URL}/api/marketplace/products/${productId}`);
      if (response.ok) {
        const data = await response.json();
        // Normaliser les données pour le composant
        setProduct({
          id: data.id,
          title: data.name,
          description: data.description_short || data.description_long || data.description || '',
          price: data.price_ttc || data.price,
          originalPrice: data.original_price || null,
          discount: data.discount_percent || 0,
          rating: data.average_rating || 4.5,
          reviewCount: data.reviews_count || 0,
          category: data.rayon_id || data.category,
          brand: data.brand || 'Marque',
          inStock: (data.stock || 0) > 0,
          stockQuantity: data.stock || 0,
          photos: data.photos?.length > 0 
            ? data.photos.map(p => typeof p === 'string' ? p : p.url) 
            : ['https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800'],
          features: data.features || [],
          variations: data.variations || [],
          seller: {
            id: data.seller_id,
            name: data.seller_name || 'Vendeur Pro',
            verified: data.seller_is_verified || false,
            rating: data.seller_rating || 4.5,
            shop_slug: data.seller_shop_slug
          },
          delivery: {
            express: data.delivery_express || false,
            free: data.delivery_free || (data.price_ttc > 50),
            delay: data.delivery_delay || '3-5 jours',
            cost: data.delivery_cost || 0
          },
          is_demo: data.is_demo || false
        });
        setLoading(false);
        return;
      }

      // FALLBACK: Chercher dans les données de test locales
      const mockProduct = MOCK_PRODUITS.find(p => p.id === productId);
      if (mockProduct) {
        setProduct(mockProduct);
        setLoading(false);
        return;
      }

      // Dernier fallback: API ecosystem (anciens produits)
      const ecosystemResponse = await fetch(`${API_URL}/api/ecosystem/products/${productId}`);
      if (ecosystemResponse.ok) {
        const data = await ecosystemResponse.json();
        setProduct({
          id: data.id,
          title: data.name || data.title,
          description: data.description,
          price: data.price_ttc || data.price,
          originalPrice: data.original_price,
          discount: data.discount_percent || 0,
          rating: data.average_rating || 4.5,
          reviewCount: data.reviews_count || 0,
          category: data.rayon_id,
          brand: data.brand,
          inStock: (data.stock || 0) > 0,
          stockQuantity: data.stock || 0,
          photos: data.photos || [],
          features: data.features || [],
          seller: {
            name: data.seller_name || 'Vendeur',
            verified: data.seller_is_verified || false,
            rating: 4.5
          },
          delivery: { express: false, free: data.price_ttc > 50, delay: '3-5 jours' }
        });
      }
    } catch (error) {
      console.error('Erreur chargement produit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      toast.success(`${quantity} article(s) ajouté(s) au panier`);
    }
  };

  const handleToggleFavorite = () => {
    if (product) {
      toggleFavorite(product);
      toast.success(isFavorite ? 'Retiré des favoris' : 'Ajouté aux favoris');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product?.title,
          text: `Découvrez ce produit sur AbracadaBati : ${product?.title}`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Partage annulé');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Lien copié dans le presse-papier');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <UnifiedHeader />
        <MainNavigation />
        <div className="h-24 md:hidden" />
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <UnifiedHeader />
        <MainNavigation />
        <div className="h-24 md:hidden" />
        <main className="max-w-3xl mx-auto px-4 py-12 text-center">
          <Package size={64} className="mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Produit introuvable</h1>
          <p className="text-gray-500 mb-6">Ce produit n'existe pas ou a été retiré du catalogue.</p>
          <Link to="/produits">
            <Button className="bg-[#1E40AF] hover:bg-[#1E3A8A]">
              Retour à la marketplace
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  const isTest = product.id?.startsWith('prod-');

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <UnifiedHeader />
      <MainNavigation />
      <div className="h-24 md:hidden" />

      {/* Header avec retour */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={20} />
              <span className="text-sm font-medium">Retour</span>
            </button>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleToggleFavorite}
                className={`p-2 rounded-lg transition-colors ${
                  isFavorite ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-600 hover:text-red-500'
                }`}
              >
                <Heart size={20} fill={isFavorite ? 'currentColor' : 'none'} />
              </button>
              <button
                onClick={handleShare}
                className="p-2 bg-gray-100 text-gray-600 hover:text-gray-900 rounded-lg transition-colors"
              >
                <Share2 size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-2 gap-8">
          
          {/* Colonne gauche - Images */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="relative aspect-square bg-gray-50">
                {product.photos && product.photos.length > 0 ? (
                  <>
                    <img
                      src={product.photos[currentImageIndex]}
                      alt={product.title}
                      className="w-full h-full object-contain p-4"
                    />
                    
                    {/* Badge TEST */}
                    {isTest && (
                      <div className="absolute top-4 left-4 bg-amber-500 text-white px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-1.5 shadow-lg">
                        <Info size={16} />
                        PRODUIT TEST
                      </div>
                    )}

                    {/* Promo badge */}
                    {product.discount > 0 && (
                      <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1.5 rounded-lg text-sm font-bold shadow-lg">
                        -{product.discount}%
                      </div>
                    )}
                    
                    {product.photos.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImageIndex(i => i > 0 ? i - 1 : product.photos.length - 1)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                        >
                          <ChevronLeft size={24} className="text-gray-700" />
                        </button>
                        <button
                          onClick={() => setCurrentImageIndex(i => i < product.photos.length - 1 ? i + 1 : 0)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                        >
                          <ChevronRight size={24} className="text-gray-700" />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package size={64} className="text-gray-300" />
                  </div>
                )}
              </div>
              
              {/* Thumbnails */}
              {product.photos && product.photos.length > 1 && (
                <div className="p-3 flex gap-2 overflow-x-auto border-t border-gray-100">
                  {product.photos.map((photo, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${
                        idx === currentImageIndex ? 'border-[#1E40AF]' : 'border-transparent'
                      }`}
                    >
                      <img src={photo} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Caractéristiques */}
            {product.features && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Caractéristiques</h3>
                <div className="grid grid-cols-2 gap-3">
                  {product.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check size={16} className="text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Colonne droite - Infos et achat */}
          <div className="space-y-4">
            
            {/* Infos produit */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              {/* Marque */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-[#1E40AF] font-medium">{product.brand}</span>
                {product.seller?.verified && (
                  <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-medium">
                    Vendeur vérifié
                  </span>
                )}
              </div>

              <h1 className="text-xl font-bold text-gray-900 mb-4">
                {product.title}
              </h1>

              {/* Notes */}
              {product.rating && (
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={16} 
                        className={i < Math.floor(product.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-900">{product.rating}</span>
                  <span className="text-sm text-gray-500">({product.reviewCount} avis)</span>
                </div>
              )}

              {/* Prix */}
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-3xl font-bold text-[#F97316]">
                  {formatPrice(product.price)}
                </span>
                {product.originalPrice && (
                  <>
                    <span className="text-lg text-gray-400 line-through">
                      {formatPrice(product.originalPrice)}
                    </span>
                    <span className="bg-red-100 text-red-600 text-sm font-bold px-2 py-1 rounded">
                      -{product.discount}%
                    </span>
                  </>
                )}
              </div>

              {/* Stock */}
              <div className="flex items-center gap-2 mb-6">
                {product.inStock ? (
                  <>
                    <Check size={18} className="text-emerald-500" />
                    <span className="text-emerald-600 font-medium">En stock</span>
                    <span className="text-gray-500 text-sm">({product.stockQuantity} disponibles)</span>
                  </>
                ) : (
                  <span className="text-red-600 font-medium">Rupture de stock</span>
                )}
              </div>

              {/* Alerte mode test */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Mode Préouverture</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Ce produit est un exemple. La marketplace sera bientôt opérationnelle !
                    </p>
                  </div>
                </div>
              </div>

              {/* Quantité */}
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-medium text-gray-700">Quantité :</span>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-12 text-center font-medium">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(q => q + 1)}
                    className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="space-y-3">
                <Button 
                  className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-xl"
                  onClick={handleAddToCart}
                >
                  <ShoppingCart size={18} className="mr-2" />
                  Ajouter au panier
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full h-12 border-2 border-[#1E40AF] text-[#1E40AF] hover:bg-[#1E40AF]/5 font-semibold rounded-xl"
                  onClick={() => toast.info('Fonctionnalité bientôt disponible !')}
                >
                  <Zap size={18} className="mr-2" />
                  Acheter maintenant
                </Button>
              </div>
            </div>

            {/* Livraison */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Livraison</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <Truck size={20} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {product.delivery?.free ? 'Livraison gratuite' : `Livraison ${product.delivery?.cost ? formatPrice(product.delivery.cost) : ''}`}
                    </p>
                    <p className="text-sm text-gray-500">Délai : {product.delivery?.delay || '3-5 jours'}</p>
                  </div>
                </div>
                {product.delivery?.express && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Zap size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Express disponible</p>
                      <p className="text-sm text-gray-500">Recevez demain</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Vendeur - Section enrichie avec IA, CGV, Contact */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Vendu par</h3>
              
              {/* Info vendeur principale */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] flex items-center justify-center overflow-hidden">
                  {seller?.logo ? (
                    <img src={seller.logo} alt={seller.name} className="w-full h-full object-cover" />
                  ) : (
                    <Store size={28} className="text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900">{seller?.name || product.seller?.name}</span>
                    {(seller?.verified || product.seller?.verified) && (
                      <Shield size={16} className="text-emerald-500" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Star size={14} className="text-amber-400 fill-amber-400" />
                    <span>{seller?.rating || product.seller?.rating || 4.5}</span>
                    <span>•</span>
                    <span>{seller?.reviewsCount || 0} avis</span>
                  </div>
                  {seller?.city && (
                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                      <MapPin size={12} />
                      <span>{seller.city}</span>
                      <span>•</span>
                      <span>Depuis {seller.createdAt}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Certifications vendeur */}
              {seller?.certifications?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {seller.certifications.map((cert, idx) => (
                    <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                      {cert}
                    </span>
                  ))}
                </div>
              )}

              {/* Boutons d'action vendeur */}
              <div className="space-y-2">
                {/* Voir la boutique */}
                <Link to={`/boutique/${seller?.slug || 'vendeur'}`}>
                  <Button 
                    variant="outline" 
                    className="w-full justify-center gap-2 border-[#1E40AF] text-[#1E40AF] hover:bg-[#1E40AF]/5"
                  >
                    <Store size={16} />
                    Voir la boutique
                    <ExternalLink size={14} />
                  </Button>
                </Link>

                {/* Assistant IA */}
                <Button 
                  variant="outline" 
                  className="w-full justify-center gap-2 border-purple-500 text-purple-600 hover:bg-purple-50"
                  onClick={() => setShowAIChat(true)}
                >
                  <Sparkles size={16} />
                  Poser une question à l'IA
                </Button>

                {/* Contacter le vendeur */}
                <Button 
                  variant="outline" 
                  className="w-full justify-center gap-2"
                  onClick={() => toast.info(`Contactez ${seller?.name} : ${seller?.email || 'contact@vendeur.fr'}`)}
                >
                  <MessageSquare size={16} />
                  Contacter le vendeur
                </Button>
              </div>

              {/* Liens CGV et Mentions légales */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between text-xs">
                  <button 
                    onClick={() => setShowCGV(true)}
                    className="text-gray-500 hover:text-[#1E40AF] flex items-center gap-1"
                  >
                    <FileText size={12} />
                    CGV du vendeur
                  </button>
                  <button 
                    onClick={() => setShowMentions(true)}
                    className="text-gray-500 hover:text-[#1E40AF] flex items-center gap-1"
                  >
                    <FileText size={12} />
                    Mentions légales
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Description</h2>
          <p className="text-gray-700 leading-relaxed">
            {product.description}
          </p>
        </div>
        
        {/* ══════════════════════════════════════════════════════════════════
            SECTION VIDÉOS & POSTS DU RÉSEAU SOCIAL
            ══════════════════════════════════════════════════════════════════ */}
        <LinkedMediaSection 
          type="products" 
          itemId={product.id || 'default'} 
          itemName={product.name} 
        />
      </main>

      {/* Footer Global */}
      

      {/* Spacer pour mobile */}
      <div className="h-24 lg:hidden" />

      {/* ═══ BARRE D'ACTIONS MOBILE FIXE - 3 BOUTONS ═══ */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-40 lg:hidden">
        <div className="flex gap-2 max-w-lg mx-auto">
          {/* Voir la boutique */}
          <button
            onClick={() => navigate(`/boutique/${seller?.slug || 'vendeur'}`)}
            className="flex-1 py-3 border-2 border-orange-500 bg-orange-50 text-orange-600 rounded-xl font-bold flex flex-col items-center justify-center gap-0.5 hover:bg-orange-100 transition-colors text-xs"
          >
            <Store size={18} />
            <span>Boutique</span>
          </button>
          
          {/* Contacter */}
          <button
            onClick={() => toast.info(`Contactez ${seller?.name} : ${seller?.email || 'contact@vendeur.fr'}`)}
            className="flex-1 py-3 border-2 border-[#1E3A5F] text-[#1E3A5F] rounded-xl font-bold flex flex-col items-center justify-center gap-0.5 hover:bg-[#1E3A5F]/5 transition-colors text-xs"
          >
            <MessageSquare size={18} />
            <span>Contacter</span>
          </button>
          
          {/* Ajouter au panier */}
          <button
            onClick={handleAddToCart}
            className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-bold flex flex-col items-center justify-center gap-0.5 hover:bg-orange-600 transition-colors text-xs"
          >
            <ShoppingCart size={18} />
            <span>Panier</span>
          </button>
        </div>
      </div>

      {/* ═══ BOUTON FLOTTANT IA MOBILE ═══ */}
      {seller && (
        <SmartAIFab 
          sellerName={seller.name} 
          onClick={() => setShowAIChat(true)} 
        />
      )}

      {/* Modal Assistant IA Vendeur */}
      {seller && (
        <VendeurAIChat 
          seller={seller} 
          isOpen={showAIChat} 
          onClose={() => setShowAIChat(false)} 
        />
      )}

      {/* Modal CGV */}
      <AnimatePresence>
        {showCGV && seller && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowCGV(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[80vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 border-b flex items-center justify-between bg-gray-50">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <FileText size={20} className="text-[#1E40AF]" />
                  Conditions Générales de Vente
                </h3>
                <button onClick={() => setShowCGV(false)} className="p-2 hover:bg-gray-200 rounded-full">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 overflow-y-auto">
                <p className="text-sm text-gray-500 mb-4">Vendeur : {seller.name}</p>
                <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">
                  {seller.cgv}
                </pre>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Mentions Légales */}
      <AnimatePresence>
        {showMentions && seller && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowMentions(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[80vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 border-b flex items-center justify-between bg-gray-50">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <FileText size={20} className="text-[#1E40AF]" />
                  Mentions Légales
                </h3>
                <button onClick={() => setShowMentions(false)} className="p-2 hover:bg-gray-200 rounded-full">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 overflow-y-auto">
                <p className="text-sm text-gray-500 mb-4">Vendeur : {seller.name}</p>
                <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">
                  {seller.mentionsLegales}
                </pre>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
